import type { WorkIndexCaseStudyId } from "~/content/work-index"

export type ServicePresentation = {
  slug: string
  label: string
  lead: string
  headline: string
  headlineAccent: string
  body: string
  outcome: string
  outcomeBody: string
  outputs: string[][]
  process: string
  processBody: string
  steps: string[][]
  artifactTitle: string
  artifact: string[]
  proofName: string
  proof: string
  proofBody: string
  proofLink: string
  proofId: WorkIndexCaseStudyId
  start: string
  startBody: string
  entry: string[][]
  bridge: string
  close: string
  closeBody: string
}

export const servicePresentations = {
  "web-development": {
    slug: "development",
    label: "DEVELOPMENT",
    lead: "Build your next product.",
    headline: "Keep improving it.",
    headlineAccent: "improving",
    body: "Bring us an idea, a prototype, or an app that needs work. We design, build, test, and release web and mobile software, with working versions for you to try along the way.",
    outcome: "Working software you\ncan put to use.",
    outcomeBody:
      "Build the product your customers need and the tools your team relies on. We connect the experience people see with the systems that make it work.",
    outputs: [
      [
        "Web + mobile products",
        "A first release or the next useful improvement to an existing app.",
      ],
      ["Connected systems", "Payments, accounts, data, and integrations working together."],
      ["A path to production", "Testing, deployment, and the operational details behind launch."],
      ["Room to keep improving", "A codebase and a working rhythm your team can build on."],
    ],
    process: "Try it. Shape it.\nKeep building.",
    processBody:
      "You see working software throughout the build. Each round gives us something concrete to discuss and a clearer next priority.",
    steps: [
      [
        "Choose the next priority",
        "Agree on the user need, scope, and what a useful result looks like.",
      ],
      [
        "Build a working version",
        "Designers and developers work through the experience and implementation.",
      ],
      [
        "Try it together",
        "Review the behavior, test the details, and give feedback on what needs to change.",
      ],
      ["Release and learn", "Put it into use, watch what happens, and decide what comes next."],
    ],
    artifactTitle: "A feature moves from an idea to a release.",
    artifact: ["Priority", "Working version", "Feedback", "Release"],
    proofName: "THE INFORMATION",
    proof: "A fixed launch date.\nA working mobile app.",
    proofBody:
      "Ten weeks for design, development, testing, and app store submissions. We launched Tech Top 10 for iOS and Android in time for The Information’s annual conference, then kept working together as the product grew.",
    proofLink: "Read The Information story →",
    start: "Start where\nyour product is.",
    startBody:
      "We’ll work out what needs attention first, whether you’re starting fresh or building on something that already exists.",
    entry: [
      ["An idea", "Define the first useful version through Sprint Zero."],
      ["An existing app", "Understand the code and the priorities before changing it."],
      [
        "An AI-built prototype",
        "Work through the gaps between a promising prototype and a product people can rely on.",
      ],
    ],
    bridge: "For ongoing work, a Pod brings the team together at a pace you choose.",
    close: "What do you want\nto build next?",
    closeBody:
      "Tell us what you have in mind and where things stand. We’ll talk through a useful next step.",
    proofId: "the-information",
  },
  "ai-strategy": {
    slug: "ai-strategy",
    label: "AI STRATEGY",
    lead: "Put AI to work",
    headline: "on something that matters.",
    headlineAccent: "that matters",
    body: "Bring us a process that takes too much time or a product idea you want to test. We’ll work out where AI could help, test it with your data, and build on what proves useful.",
    outcome: "Find a useful place\nto start.",
    outcomeBody:
      "Start with the work itself. Where does time go? What information is available? What happens when something goes wrong?",
    outputs: [
      ["A specific opportunity", "A workflow and a clear reason to improve it."],
      [
        "A working experiment",
        "Something you can try with representative examples from your work.",
      ],
      ["Evidence for the decision", "Results, failure cases, and a view of the effort involved."],
      [
        "A practical next step",
        "Improve the experiment, integrate it, or choose a different approach.",
      ],
    ],
    process: "Test it on the work\nyou actually do.",
    processBody:
      "A convincing demo is a starting point. We work through everyday cases and difficult ones so you can judge whether the result is useful.",
    steps: [
      [
        "Choose representative examples",
        "Include the common tasks and the cases that are easy to get wrong.",
      ],
      [
        "Agree on useful results",
        "Define what good looks like and where a person needs to decide.",
      ],
      ["Compare and improve", "Review the output, investigate failures, and refine the approach."],
      ["Plan for everyday use", "Connect it to the workflow, with monitoring and clear ownership."],
    ],
    artifactTitle: "Keep the decision grounded in evidence.",
    artifact: ["Real examples", "Expected result", "Observed result", "Next decision"],
    proofName: "BRIGHTLINES",
    proof: "AI that fits into\nthe analyst’s work.",
    proofBody:
      "Brightlines’ Verdict AI combines rules and an LLM to categorize search results and process data-broker findings, with analysts in the loop. At Leap Fund, our work also includes guidebook retrieval and plain-language access to program data.",
    proofLink: "Read the Brightlines story →",
    start: "Bring us\none process.",
    startBody:
      "You don’t need a finished AI strategy. A specific task and a few examples give us a useful place to begin.",
    entry: [
      ["The work", "Show us the task that takes too long or holds up the team."],
      [
        "The examples",
        "Bring the inputs, the current output, and what a better result would look like.",
      ],
      [
        "The constraints",
        "Tell us who uses the result, what data is involved, and which mistakes matter.",
      ],
    ],
    bridge:
      "We build with AI every day. AllSpark is one of the tools we use to put that experience into practice.",
    close: "Which part of the\nwork takes too long?",
    closeBody:
      "Bring a workflow or a product question. We’ll talk through whether AI could help and what it would take to find out.",
    proofId: "brightlines",
  },
  "sprint-zero": {
    slug: "sprint-zero",
    label: "SPRINT ZERO",
    lead: "Know what you’re building.",
    headline: "And what it will take.",
    headlineAccent: "will take",
    body: "Turn an idea or a complicated product question into a clear first build. Together, we define the essential workflows, explore the design, test technical assumptions, and map the work ahead.",
    outcome: "A clear starting point\nfor the build.",
    outcomeBody:
      "Sprint Zero is a focused planning and design engagement. We work through the decisions that make the next stage of development clearer.",
    outputs: [
      ["Who it’s for", "The people using the product and the jobs it needs to help them do."],
      ["What comes first", "The essential workflows and a manageable first version."],
      ["What needs testing", "Technical questions and assumptions worth investigating early."],
      ["What can wait", "A deliberate boundary around the first build."],
    ],
    process: "Make the idea\ntangible.",
    processBody:
      "Conversations become flows, prototypes, and technical findings. The output gives everyone something specific to react to.",
    steps: [
      [
        "Understand the product",
        "Work through the goals, users, current context, and constraints.",
      ],
      ["Explore the experience", "Map key journeys and make the important screens tangible."],
      [
        "Investigate the unknowns",
        "Test the integrations and technical assumptions that could change the plan.",
      ],
      [
        "Agree on the first build",
        "Prioritize the work and make scope, trade-offs, and estimates explicit.",
      ],
    ],
    artifactTitle: "A plan connected to the product.",
    artifact: ["Product brief", "Key journeys", "Prototype", "Build plan"],
    proofName: "THE FOREM",
    proof: "From a pitch deck\nto a product people\ncould try.",
    proofBody:
      "Sprint Zero helped The Forem define its users, workflows, and core product concepts. An interactive prototype supported fundraising and product validation before the work moved into an MVP.",
    proofLink: "Read The Forem story →",
    start: "What you\nleave with.",
    startBody:
      "We agree on the depth of each deliverable around the decisions your project needs. Every engagement has a clear scope.",
    entry: [
      ["Product direction", "A brief and prioritized scope that describe the first version."],
      ["Flows + designs", "Key user journeys and the agreed screens or prototype."],
      [
        "Technical findings + build plan",
        "Architecture decisions, investigation results, milestones, and estimates.",
      ],
    ],
    bridge: "Use the work to plan development with Tanooki or with your own team.",
    close: "Let’s work out\nthe first version.",
    closeBody:
      "Tell us about the idea and the questions around it. We’ll identify the decisions worth making before the build.",
    proofId: "the-forem",
  },
  "design": {
    slug: "design",
    label: "PRODUCT DESIGN",
    lead: "Make your product",
    headline: "easier to use.",
    headlineAccent: "easier to use",
    body: "Help people understand what to do and get it done. We work through the flows, screens, and details, then give your team a design system it can build and maintain.",
    outcome: "Make the important\nthings easier.",
    outcomeBody:
      "Good design helps people finish the task in front of them. We start with where the experience gets confusing, slow, or difficult.",
    outputs: [
      ["Clearer onboarding", "Help someone understand the product and take the first useful step."],
      ["Simpler workflows", "Make complicated tasks easier to follow and complete."],
      [
        "Consistent screens",
        "Use familiar patterns so people don’t have to learn the interface again.",
      ],
      ["A buildable system", "Components and behavior that carry through into implementation."],
    ],
    process: "Work through the\nexperience before\nthe build.",
    processBody:
      "Make the important decisions while they’re easy to change. Review a flow, try a prototype, and refine the details together.",
    steps: [
      ["Understand the task", "Start with the people, their goals, and the places they get stuck."],
      ["Map the flow", "Connect the steps and decide what each screen needs to do."],
      ["Try the prototype", "Walk through the experience and test the assumptions."],
      [
        "Prepare it for development",
        "Define components, states, responsive behavior, and implementation details.",
      ],
    ],
    artifactTitle: "From a task to a buildable interface.",
    artifact: ["User journey", "Wireframes", "Prototype", "Design system"],
    proofName: "HADLEY",
    proof: "A simple contribution.\nA complicated system\nbehind it.",
    proofBody:
      "Hadley wanted contributing to a child’s education savings to feel straightforward. We worked through the contribution flow and the financial systems behind it, connecting a clear mobile experience to the payment process.",
    proofLink: "Read the Hadley story →",
    start: "A new product.\nOr a better\nexisting one.",
    startBody:
      "The starting point can be a blank page, a difficult workflow, or a product that has grown out of its original design.",
    entry: [
      ["Shape a new experience", "Explore the key flows and screens before development."],
      ["Improve a difficult task", "Focus the work around a specific part of the product."],
      ["Bring the interface together", "Build reusable patterns and a coherent visual system."],
    ],
    bridge: "Brand identity can be part of the engagement when the product needs it.",
    close: "Where are people\ngetting stuck?",
    closeBody:
      "Show us the product or describe the experience you want to create. We’ll talk through where design can help.",
    proofId: "hadley",
  },
  "product": {
    slug: "product",
    label: "PRODUCT MANAGEMENT",
    lead: "Know what to build next.",
    headline: "Keep the work moving.",
    headlineAccent: "moving",
    body: "Turn competing requests into clear priorities. We work with you, your designers, and your developers to define the work, make trade-offs, and learn from what ships.",
    outcome: "Clear priorities.\nFewer loose ends.",
    outcomeBody:
      "Someone needs to connect the business goals to the day-to-day build. We keep the decisions, details, and people moving together.",
    outputs: [
      ["A prioritized roadmap", "A shared view of what matters now and what comes later."],
      ["Work ready to build", "Clear requirements, expected behavior, and acceptance criteria."],
      [
        "Visible trade-offs",
        "Scope, budget, and dependencies raised while there’s time to decide.",
      ],
      ["A feedback loop", "What we learn from releases shapes the next round of work."],
    ],
    process: "From a request\nto a release.",
    processBody:
      "Product management follows the feature through the whole process. The work stays connected to the problem it is meant to solve.",
    steps: [
      ["Define the need", "Understand the request, the user, and the result we’re aiming for."],
      ["Decide the priority", "Compare it with other work and make the trade-offs explicit."],
      [
        "Support the build",
        "Resolve questions with design and engineering and keep dependencies visible.",
      ],
      [
        "Accept, release, and learn",
        "Check the behavior against the agreed need and use feedback to guide what’s next.",
      ],
    ],
    artifactTitle: "A shared view of the work.",
    artifact: ["Now", "Next", "Decisions needed", "Learning"],
    proofName: "PEARLAND BROKERAGE",
    proof: "Keep improving\nthe whole operation.",
    proofBody:
      "Pearland’s platform connects customer renewals and payments with the workflows its staff use behind the scenes. The engagement grew from an online launch into ongoing development across policy systems, payments, and customer notices.",
    proofLink: "Read the Pearland story →",
    start: "Work with your\nteam or ours.",
    startBody:
      "We can help organize an existing build or bring product management into a Tanooki team. Start with the decisions and responsibilities that need attention.",
    entry: [
      ["Set the direction", "Turn goals and requests into a practical roadmap."],
      [
        "Run the ongoing work",
        "Keep scope, priorities, and decisions visible as the product evolves.",
      ],
      [
        "Connect the team",
        "Give design, engineering, and stakeholders a shared understanding of the work.",
      ],
    ],
    bridge: "Product management is part of every Pod, alongside design, development, and QA.",
    close: "What needs\na clearer plan?",
    closeBody:
      "Tell us where the project stands and what keeps getting stuck. We’ll talk through a useful way forward.",
    proofId: "pearland",
  },
} satisfies Record<string, ServicePresentation>
